import React, { useMemo, useState, useEffect } from 'react';
import XCircleIcon from '../icons/XCircleIcon';
import type { StaffMember, Order } from '../../types';
import { useSoftKeyboardField } from '../../hooks/useSoftKeyboardField';
import { isManager } from '../../lib/authUtils';

interface CancelOrderApprovalModalProps {
  isOpen: boolean;
  onClose: () => void;
  onApprove: (staffId: number, reason: string) => void;
  order: Order;
  staffMembers: StaffMember[];
  currentUser?: StaffMember;
  requiresApproval: boolean;
  cancellationReason?: string; // Special reason like "customer doesn't have money"
}

const CancelOrderApprovalModal: React.FC<CancelOrderApprovalModalProps> = ({
  isOpen,
  onClose,
  onApprove,
  order,
  staffMembers,
  currentUser,
  requiresApproval,
  cancellationReason
}) => {
  const [selectedManagerId, setSelectedManagerId] = useState<number | null>(
    currentUser?.id || null
  );
  const [approvalReason, setApprovalReason] = useState(cancellationReason || '');
  const [pin, setPin] = useState('');

  if (!isOpen) return null;

  // Filter staff to show only managers
  // Use the same logic as isManager() from authUtils to ensure consistency
  const managers = useMemo(() => {
    const managerList = staffMembers.filter(staff => {
      // Use the isManager function for consistency
      const isManagerByRole = isManager(staff);
      
      // Also check for permissions that indicate manager status (for CurrentUser objects)
      const hasManagerPermissions = (staff as any).permissions?.has('access_reports') ||
                                   (staff as any).permissions?.has('manage_staff');
      
      // Include if role contains 'manager' or 'admin' (for flexibility with role names)
      const roleLower = staff.role?.toLowerCase() || '';
      const hasManagerInRole = roleLower.includes('manager') || roleLower.includes('admin');
      
      // Always include current user
      const isCurrentUser = staff.id === currentUser?.id;
      
      return isManagerByRole || hasManagerPermissions || hasManagerInRole || isCurrentUser;
    });
    
    // Debug logging to see what managers are found
    console.log('🔍 Available managers for cancellation approval:', {
      totalStaff: staffMembers.length,
      managersFound: managerList.length,
      allStaff: staffMembers.map(s => ({ 
        id: s.id, 
        name: s.name, 
        role: s.role, 
        isAdmin: s.isAdmin,
        isManager: isManager(s),
        hasAccessReports: (s as any).permissions?.has('access_reports'),
        hasManageStaff: (s as any).permissions?.has('manage_staff')
      })),
      managerNames: managerList.map(m => ({ id: m.id, name: m.name, role: m.role, isAdmin: m.isAdmin }))
    });
    
    return managerList;
  }, [staffMembers, currentUser]);

  const selectedManager = managers.find(m => m.id === selectedManagerId);

  const pinValidator = (value: string) => /^\d{0,6}$/.test(value);
  
  // Handle PIN input changes - support both soft keyboard and direct input
  const handlePinChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    if (pinValidator(value)) {
      setPin(value);
      console.log('🔑 PIN changed via direct input:', { value, length: value.length });
    }
  };
  
  const pinField = useSoftKeyboardField({
    id: 'cancel-order-manager-pin',
    type: 'numeric',
    label: selectedManager ? `PIN for ${selectedManager.name}` : 'Manager PIN',
    value: pin,
    onChange: (value) => {
      if (pinValidator(value)) {
        setPin(value);
        console.log('🔑 PIN changed via soft keyboard:', { value, length: value.length });
      }
    },
    manualValidator: pinValidator,
    maskValue: true,
  });
  
  // Debug logging for button state
  useEffect(() => {
    if (requiresApproval && selectedManager) {
      const noManager = selectedManagerId === null || selectedManagerId === undefined;
      const isDisabled = noManager || !approvalReason.trim() || !pin || pin.length < 4;
      console.log('🔍 Approve button state check:', {
        selectedManagerId,
        selectedManagerName: selectedManager?.name,
        hasApprovalReason: !!approvalReason.trim(),
        approvalReasonLength: approvalReason.trim().length,
        pinLength: pin.length,
        pinValue: pin ? pin.replace(/./g, '*') : '(empty)', // Masked for security
        isDisabled,
        disabledReasons: {
          noManager: noManager,
          noReason: !approvalReason.trim(),
          noPin: !pin || pin.length < 4
        }
      });
    }
  }, [requiresApproval, selectedManagerId, approvalReason, pin, selectedManager]);
  const reasonField = useSoftKeyboardField({
    id: 'cancel-order-approval-reason',
    type: 'alphanumeric',
    label: 'Cancellation Reason',
    value: approvalReason,
    onChange: setApprovalReason,
  });

  const handleApprove = () => {
    if (selectedManagerId === null || selectedManagerId === undefined) {
      alert('Please select a manager');
      return;
    }
    if (!approvalReason.trim()) {
      alert('Please provide a reason for cancellation');
      return;
    }
    if (selectedManager && selectedManager.pin !== pin) {
      alert('Invalid PIN. Please try again.');
      setPin('');
      return;
    }
    onApprove(selectedManagerId, approvalReason.trim());
    handleClose();
  };

  const handleClose = () => {
    setSelectedManagerId(currentUser?.id || null);
    setApprovalReason(cancellationReason || '');
    setPin('');
    onClose();
  };

  const orderTotal = order.totalAmount || order.items.reduce((sum, item) => sum + item.price * item.quantity, 0);
  const orderStatus = order.status;
  const isPaid = order.paymentStatus === 'paid';

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 overflow-y-auto">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-md max-h-[90vh] overflow-y-auto my-auto mx-4">
        <div className="flex items-center justify-between p-4 border-b">
          <h2 className="text-xl font-bold text-gray-800">
            {requiresApproval ? 'Manager Approval Required' : 'Cancel Order'}
          </h2>
          <button
            onClick={handleClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <XCircleIcon className="w-6 h-6" />
          </button>
        </div>

        <div className="p-4 space-y-4">
          {/* Order Summary */}
          <div className="bg-red-50 border border-red-400 rounded-md p-4">
            <h3 className="font-semibold text-red-800 mb-2">Order Details</h3>
            <div className="space-y-1 text-sm">
              <div className="flex justify-between">
                <span className="text-red-700">Order ID:</span>
                <span className="font-semibold">{order.id}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-red-700">Order Status:</span>
                <span className="font-semibold capitalize">{orderStatus}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-red-700">Payment Status:</span>
                <span className="font-semibold capitalize">{order.paymentStatus}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-red-700">Order Total:</span>
                <span className="font-semibold">${orderTotal.toFixed(2)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-red-700">Items:</span>
                <span className="font-semibold">{order.items.length}</span>
              </div>
              {isPaid && (
                <div className="pt-2 border-t border-red-300">
                  <p className="text-red-800 font-bold text-xs">
                    ⚠️ This order has been paid. Cancelling will require a refund.
                  </p>
                </div>
              )}
            </div>
          </div>

          {requiresApproval && (
            <>
              {/* Manager Selection */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Approving Manager *
                </label>
                <select
                  value={selectedManagerId || ''}
                  onChange={(e) => setSelectedManagerId(parseInt(e.target.value))}
                  className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-pink-500 focus:border-pink-500"
                >
                  <option value="">Select a manager...</option>
                  {managers.length > 0 ? (
                    managers.map(manager => (
                      <option key={manager.id} value={manager.id}>
                        {manager.name} ({manager.role || 'No role'})
                      </option>
                    ))
                  ) : (
                    // Fallback: show all staff if no managers found (for debugging)
                    <>
                      <option value="" disabled>No managers found. Showing all staff:</option>
                      {staffMembers.map(staff => (
                        <option key={staff.id} value={staff.id}>
                          {staff.name} ({staff.role || 'No role'})
                        </option>
                      ))}
                    </>
                  )}
                </select>
              </div>

              {/* PIN Entry */}
              {selectedManager && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    PIN for {selectedManager.name} *
                  </label>
                  <input
                    type="password"
                    inputMode="numeric"
                    maxLength={6}
                    value={pin}
                    onChange={handlePinChange}
                    {...pinField}
                    className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-pink-500 focus:border-pink-500"
                    placeholder="Enter PIN"
                  />
                </div>
              )}
            </>
          )}

          {/* Cancellation Reason */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Cancellation Reason *
            </label>
            <textarea
              value={approvalReason}
              {...reasonField}
              className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-pink-500 focus:border-pink-500"
              rows={3}
              placeholder="e.g., Customer doesn't have money, Order placed by mistake, Customer left without paying..."
              required
            />
          </div>

          {/* Action Buttons */}
          <div className="flex gap-2 pt-2">
            <button
              onClick={handleClose}
              className="flex-1 py-2 px-4 bg-gray-200 text-gray-700 rounded-md font-semibold hover:bg-gray-300 transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleApprove}
              disabled={requiresApproval && (selectedManagerId === null || selectedManagerId === undefined || !approvalReason.trim() || !pin || pin.length < 4)}
              className="flex-1 py-2 px-4 bg-red-500 text-white rounded-md font-semibold hover:bg-red-600 transition-colors disabled:bg-gray-300 disabled:text-gray-500 disabled:cursor-not-allowed"
            >
              {requiresApproval ? 'Approve Cancellation' : 'Confirm Cancellation'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CancelOrderApprovalModal;
